import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('reddi_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('reddi_token');
      localStorage.removeItem('reddi_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Driver APIs
export const driverAPI = {
  createProfile: (data) => api.post('/driver/profile', data),
  getProfile: () => api.get('/driver/profile'),
  updateStatus: (isOnline) => api.put(`/driver/status?is_online=${isOnline}`),
  updateLocation: (data) => api.put('/driver/location', data),
  getEarnings: () => api.get('/driver/earnings'),
  getActiveRides: () => api.get('/driver/rides/active'),
  getAvailableRides: () => api.get('/driver/rides/available'),
};

// Rider APIs
export const riderAPI = {
  getProfile: () => api.get('/rider/profile'),
  getLoyalty: () => api.get('/rider/loyalty'),
  addPaymentMethod: (data) => api.post('/rider/payment-method', data),
  getPaymentMethods: () => api.get('/rider/payment-methods'),
  deletePaymentMethod: (id) => api.delete(`/rider/payment-method/${id}`),
};

// Ride APIs
export const rideAPI = {
  getEstimate: (data) => api.post('/rides/estimate', data),
  requestRide: (data) => api.post('/rides/request', data),
  acceptRide: (rideId) => api.post(`/rides/${rideId}/accept`),
  startRide: (rideId) => api.post(`/rides/${rideId}/start`),
  completeRide: (rideId) => api.post(`/rides/${rideId}/complete`),
  cancelRide: (rideId) => api.post(`/rides/${rideId}/cancel`),
  addTip: (rideId, tipPercentage) => api.post(`/rides/${rideId}/tip`, { tip_percentage: tipPercentage }),
  getHistory: () => api.get('/rides/history'),
  getRide: (rideId) => api.get(`/rides/${rideId}`),
  getScheduledRides: () => api.get('/rides/scheduled/upcoming'),
};

// Nearby drivers
export const getNearbyDrivers = (lat, lng, radius = 5) => 
  api.get(`/drivers/nearby?lat=${lat}&lng=${lng}&radius=${radius}`);

// Payment APIs
export const paymentAPI = {
  createCheckout: (rideId) => api.post(`/payments/create-checkout?ride_id=${rideId}`),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Notification APIs
export const notificationAPI = {
  getAll: (limit = 20, unreadOnly = false) => api.get(`/notifications?limit=${limit}&unread_only=${unreadOnly}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Rating APIs
export const ratingAPI = {
  rateRide: (rideId, rating, feedback) => api.post(`/rides/${rideId}/rate`, { ride_id: rideId, rating, feedback }),
  getDriverRatings: (limit = 20) => api.get(`/driver/ratings?limit=${limit}`),
};

// Promo Code APIs
export const promoAPI = {
  validate: (code, rideAmount) => api.post('/promo/validate', { code, ride_amount: rideAmount }),
  apply: (rideId, code) => api.post(`/promo/apply?ride_id=${rideId}&code=${code}`),
  getAvailable: () => api.get('/promo/available'),
};

// Driver Payout APIs
export const payoutAPI = {
  addBankAccount: (data) => api.post('/driver/bank-account', data),
  getBankAccounts: () => api.get('/driver/bank-accounts'),
  deleteBankAccount: (id) => api.delete(`/driver/bank-account/${id}`),
  requestPayout: (amount, bankAccountId) => api.post('/driver/payout/request', { amount, bank_account_id: bankAccountId }),
  getPayoutHistory: (limit = 20) => api.get(`/driver/payouts?limit=${limit}`),
  getBalance: () => api.get('/driver/balance'),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (userType, limit = 50, skip = 0) => api.get(`/admin/users?user_type=${userType || ''}&limit=${limit}&skip=${skip}`),
  getRides: (status, limit = 50, skip = 0) => api.get(`/admin/rides?status=${status || ''}&limit=${limit}&skip=${skip}`),
  createPromo: (data) => api.post('/admin/promo', data),
  getPromos: () => api.get('/admin/promos'),
  updatePromo: (promoId, isActive) => api.put(`/admin/promo/${promoId}?is_active=${isActive}`),
  deletePromo: (promoId) => api.delete(`/admin/promo/${promoId}`),
  getPayouts: (status, limit = 50) => api.get(`/admin/payouts?status=${status || ''}&limit=${limit}`),
  processPayout: (payoutId, action) => api.put(`/admin/payout/${payoutId}/process?action=${action}`),
  getRevenue: (days = 30) => api.get(`/admin/revenue?days=${days}`),
  getConfig: () => api.get('/admin/config'),
  registerAdmin: (data) => api.post(`/admin/register?email=${data.email}&password=${data.password}&full_name=${data.full_name}&phone=${data.phone}&secret_key=${data.secret_key}`),
};

// Surge & System APIs
export const systemAPI = {
  getSurgeStatus: () => api.get('/system/surge-status'),
};

// Driver Scheduling APIs
export const scheduleAPI = {
  setSchedule: (slots, timezone) => api.post('/driver/schedule', { slots, timezone }),
  getSchedule: () => api.get('/driver/schedule'),
  clearSchedule: () => api.delete('/driver/schedule'),
};

// Driver Document APIs
export const documentAPI = {
  getDocumentTypes: () => api.get('/documents/types'),
  uploadDocument: (data) => api.post('/documents/upload', data),
  getMyDocuments: () => api.get('/documents/my-documents'),
  getVerificationStatus: () => api.get('/documents/verification-status'),
  getDocument: (documentId) => api.get(`/documents/${documentId}`),
  downloadDocument: (documentId) => api.get(`/documents/${documentId}/download`),
  // Admin endpoints
  getPendingDocuments: (limit = 50) => api.get(`/documents/admin/pending?limit=${limit}`),
  getDriverDocuments: (driverId) => api.get(`/documents/admin/driver/${driverId}`),
  reviewDocument: (documentId, action, reason) => api.post(`/documents/admin/${documentId}/review`, { action, reason }),
};

// Matching APIs
export const matchingAPI = {
  getConfig: () => api.get('/matching/config'),
  getStatus: (rideId) => api.get(`/matching/status/${rideId}`),
  checkSurge: (lat, lng) => api.post('/matching/surge-check', { lat, lng }),
  getNearbyDrivers: (lat, lng, radius = 5) => api.get(`/matching/drivers/nearby?lat=${lat}&lng=${lng}&radius=${radius}`),
  getPendingOffer: () => api.get('/matching/offer/pending'),
  acceptOffer: (rideId) => api.post('/matching/offer/accept', { ride_id: rideId }),
  declineOffer: (rideId) => api.post('/matching/offer/decline', { ride_id: rideId }),
  triggerFailover: (rideId) => api.post(`/matching/failover/${rideId}`),
};

// Safety & Tracking APIs
export const safetyAPI = {
  // Trip Tracking
  startTracking: (rideId) => api.post(`/safety/tracking/${rideId}/start`),
  updateLocation: (rideId, lat, lng, speed, heading) => api.post(`/safety/tracking/${rideId}/update`, { lat, lng, speed_mph: speed, heading }),
  endTracking: (rideId) => api.post(`/safety/tracking/${rideId}/end`),
  getTracking: (rideId) => api.get(`/safety/tracking/${rideId}`),
  getSharedTracking: (shareToken) => api.get(`/safety/share/${shareToken}`),
  
  // SOS
  triggerSOS: (lat, lng, rideId, message) => api.post('/safety/sos', { lat, lng, ride_id: rideId, message }),
  getActiveSOS: () => api.get('/safety/sos/active'),
  resolveSOS: (sosId) => api.post(`/safety/sos/${sosId}/resolve`),
  
  // Incidents
  getIncidentCategories: () => api.get('/safety/incidents/categories'),
  reportIncident: (rideId, category, description, evidenceUrls) => api.post('/safety/incidents/report', { ride_id: rideId, category, description, evidence_urls: evidenceUrls }),
  getMyIncidents: () => api.get('/safety/incidents/my-reports'),
  getIncident: (incidentId) => api.get(`/safety/incidents/${incidentId}`),
  
  // Ratings
  rateRide: (rideId, rating, feedback, categories) => api.post(`/safety/rate/${rideId}`, { rating, feedback, categories }),
  getUserRatings: (userId) => api.get(`/safety/ratings/${userId}`),
};

// Admin V2 APIs
export const adminV2API = {
  // Live Trips
  getLiveTrips: () => api.get('/admin-v2/live-trips'),
  
  // User Management
  getUsers: (params) => api.get('/admin-v2/users', { params }),
  getUserDetails: (userId) => api.get(`/admin-v2/users/${userId}`),
  suspendUser: (userId, reason, durationDays) => api.post(`/admin-v2/users/${userId}/suspend`, { reason, duration_days: durationDays }),
  banUser: (userId, reason) => api.post(`/admin-v2/users/${userId}/ban`, { reason }),
  reactivateUser: (userId, notes) => api.post(`/admin-v2/users/${userId}/reactivate`, { notes }),
  deleteUser: (userId, reason) => api.delete(`/admin-v2/users/${userId}`, { data: { reason } }),
  getUserActions: (userId) => api.get(`/admin-v2/users/${userId}/actions`),
  updateVerification: (userId, type, verified) => api.post(`/admin-v2/users/${userId}/verification`, { verification_type: type, verified }),
  
  // Admin Management
  getAdmins: () => api.get('/admin-v2/admins'),
  grantAdmin: (userId) => api.post('/admin-v2/admins/grant', { user_id: userId }),
  revokeAdmin: (userId, reason) => api.post(`/admin-v2/admins/${userId}/revoke`, { reason }),
  getAdminActivity: (limit = 50) => api.get(`/admin-v2/admins/activity?limit=${limit}`),
  
  // Incidents
  getIncidents: (params) => api.get('/admin-v2/incidents', { params }),
  getIncidentStats: () => api.get('/admin-v2/incidents/stats'),
  getIncident: (incidentId) => api.get(`/admin-v2/incidents/${incidentId}`),
  updateIncidentStatus: (incidentId, status, notes) => api.put(`/admin-v2/incidents/${incidentId}/status`, { status, notes }),
  resolveIncident: (incidentId, resolution, notes, action) => api.post(`/admin-v2/incidents/${incidentId}/resolve`, { resolution, resolution_notes: notes, action_taken: action }),
  
  // Fare Management
  overrideFare: (rideId, newFare, reason) => api.post(`/admin-v2/rides/${rideId}/override-fare`, { new_fare: newFare, reason }),
  processRefund: (rideId, amount, reason, type) => api.post(`/admin-v2/rides/${rideId}/refund`, { amount, reason, refund_type: type }),
  
  // System Health
  getSystemHealth: () => api.get('/admin-v2/health'),
  getRidesByHour: (days) => api.get(`/admin-v2/health/rides-by-hour?days=${days}`),
  getRevenueByDay: (days) => api.get(`/admin-v2/health/revenue-by-day?days=${days}`),
  getDriverPerformance: () => api.get('/admin-v2/health/driver-performance'),
  getAPIStats: (hours) => api.get(`/admin-v2/health/api-stats?hours=${hours}`),
};

// Governance & Metrics APIs
export const governanceAPI = {
  // Guardrails
  getGuardrailsConfig: () => api.get('/governance/guardrails/config'),
  updateGuardrailsConfig: (updates) => api.put('/governance/guardrails/config', updates),
  getPendingDecisions: () => api.get('/governance/decisions/pending'),
  reviewDecision: (decisionId, approved, override, notes) => api.post(`/governance/decisions/${decisionId}/review`, { approved, override_outcome: override, notes }),
  getAuditLog: (params) => api.get('/governance/decisions/audit', { params }),
  getDecisionTypes: () => api.get('/governance/decisions/types'),
  getBiasMetrics: () => api.get('/governance/bias/metrics'),
  
  // Metrics
  getWeeklyMetrics: (weeksBack = 0) => api.get(`/governance/metrics/weekly?weeks_back=${weeksBack}`),
  saveWeeklyReport: () => api.post('/governance/metrics/weekly/save'),
  getMetricsHistory: (weeks = 12) => api.get(`/governance/metrics/history?weeks=${weeks}`),
  getDropoffMetrics: () => api.get('/governance/metrics/dropoff'),
  getUsageMetrics: (eventType, days = 7) => api.get('/governance/metrics/usage', { params: { event_type: eventType, days } }),
  
  // Feedback
  getSurveyTemplate: (type) => api.get(`/governance/feedback/survey/${type}`),
  submitSurvey: (surveyType, rideId, responses) => api.post('/governance/feedback/survey', { survey_type: surveyType, ride_id: rideId, responses }),
  getMySurveys: () => api.get('/governance/feedback/my-surveys'),
  getFeedbackPrompts: () => api.get('/governance/feedback/prompts'),
  getBugCategories: () => api.get('/governance/feedback/bugs/categories'),
  submitBugReport: (category, title, description, deviceInfo, screenshots, rideId) => api.post('/governance/feedback/bugs', { category, title, description, device_info: deviceInfo, screenshots, ride_id: rideId }),
  getBugReports: (params) => api.get('/governance/feedback/bugs', { params }),
  updateBugReport: (reportId, updates) => api.put(`/governance/feedback/bugs/${reportId}`, updates),
  getFeedbackAnalytics: (days = 30) => api.get(`/governance/feedback/analytics?days=${days}`),
};

// Legal APIs
export const legalAPI = {
  listDocuments: () => api.get('/legal/'),
  getAllDocuments: () => api.get('/legal/all'),
  getDocument: (type) => api.get(`/legal/${type}`),
  getTerms: () => api.get('/legal/terms'),
  getPrivacy: () => api.get('/legal/privacy'),
  getDriverAgreement: () => api.get('/legal/driver_agreement'),
  getInsurance: () => api.get('/legal/insurance'),
  getRegulations: () => api.get('/legal/regulations'),
};

export default api;
